// src/pages/auth/RegisterPage.jsx - UPDATED with registratienummer field
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/marketing/Navbar';
import Turnstile from '../../components/common/Turnstile';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    beroepsgroep: 'dietist',
    registratienummer: '',
    termsAccepted: false,
    website: '' // honeypot - real users never see or fill this field
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  // Timestamp the form mounted, so the server can reject implausibly fast submissions
  const formRenderedAtRef = useRef(Date.now());

  // BIG-nummer (11 digits) or KP-nummer (11 digits, optionally with a trailing
  // specialization letter) — mirrors the server-side check in server/routes/auth.js
  const REGISTRATIENUMMER_REGEX = /^\d{11}[A-Za-z]?$/;

  // Searchable dropdown state
  const [beroepsgroepSearch, setBeroepsgroepSearch] = useState('Diëtist');
  const [beroepsgroepOpen, setBeroepsgroepOpen] = useState(false);
  const beroepsgroepRef = useRef(null);

  const beroepsgroepen = [
    { value: 'dietist', label: 'Diëtist' },
    { value: 'ergotherapeut', label: 'Ergotherapeut' },
    { value: 'logopedist', label: 'Logopedist' },
    { value: 'oefentherapeut', label: 'Oefentherapeut' },
    { value: 'podotherapeut', label: 'Podotherapeut' },
    { value: 'huidtherapie', label: 'Huidtherapeut' },
    { value: 'optometrie', label: 'Optometrist' },
    { value: 'orthoptie', label: 'Orthoptist' },
    { value: 'mbb', label: 'MBB' }
  ];

  // Filter beroepsgroepen based on search
  const filteredBeroepsgroepen = beroepsgroepen.filter(bg =>
    bg.label.toLowerCase().includes(beroepsgroepSearch.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (beroepsgroepRef.current && !beroepsgroepRef.current.contains(event.target)) {
        setBeroepsgroepOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBeroepsgroepSelect = (bg) => {
    setFormData(prev => ({ ...prev, beroepsgroep: bg.value }));
    setBeroepsgroepSearch(bg.label);
    setBeroepsgroepOpen(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setApiError('');
  };

  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Voornaam is verplicht';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Achternaam is verplicht';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'E-mailadres is verplicht';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Voer een geldig e-mailadres in';
    }

    // Registration number validation
    const cleanedRegistratienummer = formData.registratienummer.trim().replace(/[\s-]/g, '');
    if (!cleanedRegistratienummer) {
      newErrors.registratienummer = 'Registratienummer is verplicht';
    } else if (!REGISTRATIENUMMER_REGEX.test(cleanedRegistratienummer)) {
      newErrors.registratienummer = 'Vul een geldig BIG- of KP-registratienummer in (11 cijfers, eventueel gevolgd door een letter)';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Wachtwoord is verplicht';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Wachtwoord moet minimaal 8 tekens bevatten';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Wachtwoord moet een hoofdletter, kleine letter en cijfer bevatten';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Bevestig je wachtwoord';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Wachtwoorden komen niet overeen';
    }

    // Terms validation
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'Je moet akkoord gaan met de voorwaarden';
    }

    // Captcha validation
    if (!turnstileToken) {
      newErrors.turnstile = 'Bevestig dat je geen robot bent';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          beroepsgroep: formData.beroepsgroep,
          registratienummer: formData.registratienummer,
          website: formData.website,
          formRenderedAt: formRenderedAtRef.current,
          turnstileToken
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Success - show message and redirect
        navigate('/register-success', {
          state: {
            email: formData.email,
            message: data.message
          }
        });
      } else {
        // API error
        setApiError(data.message || 'Registratie mislukt. Probeer het opnieuw.');
        setTurnstileToken('');
        turnstileRef.current?.reset();
      }
    } catch (error) {
      console.error('Registration error:', error);
      setApiError('Er is een fout opgetreden. Controleer je internetverbinding en probeer het opnieuw.');
      setTurnstileToken('');
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8 mt-8">
          <h1 className="text-3xl font-light text-purple-800 mb-2 text-center">
            Account Aanmaken
          </h1>
          <p className="text-center text-gray-600 mb-6">
            Maak een account aan om te beginnen met leren
          </p>

          {apiError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Honeypot field - hidden from real users, left empty by them.
                Positioned off-screen rather than display:none/type=hidden since
                some bots specifically skip those. Never remove the name "website". */}
            <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* First name + Last name */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  Voornaam *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Jan"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>
              <div className="flex-1">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Achternaam *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="de Vries"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-mailadres *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="jan@voorbeeld.nl"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Beroepsgroep - Searchable Dropdown */}
            <div ref={beroepsgroepRef} className="relative">
              <label htmlFor="beroepsgroep" className="block text-sm font-medium text-gray-700 mb-1">
                Beroepsgroep *
              </label>
              <input
                type="text"
                id="beroepsgroep"
                value={beroepsgroepSearch}
                onChange={(e) => {
                  setBeroepsgroepSearch(e.target.value);
                  setBeroepsgroepOpen(true);
                }}
                onFocus={() => setBeroepsgroepOpen(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Zoek of selecteer beroepsgroep..."
                autoComplete="off"
              />
              {beroepsgroepOpen && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredBeroepsgroepen.length > 0 ? (
                    filteredBeroepsgroepen.map(bg => (
                      <li
                        key={bg.value}
                        onClick={() => handleBeroepsgroepSelect(bg)}
                        className={`px-3 py-2 cursor-pointer hover:bg-purple-50 ${
                          formData.beroepsgroep === bg.value ? 'bg-purple-100 text-purple-800' : ''
                        }`}
                      >
                        {bg.label}
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-gray-500 italic">
                      Geen resultaten gevonden
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* Registratienummer - NEW FIELD */}
            <div>
              <label htmlFor="registratienummer" className="block text-sm font-medium text-gray-700 mb-1">
                Registratienummer voor accreditatie *
              </label>
              <input
                type="text"
                id="registratienummer"
                name="registratienummer"
                value={formData.registratienummer}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.registratienummer ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder=""
              />
              {errors.registratienummer && (
                <p className="mt-1 text-sm text-red-600">{errors.registratienummer}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Bijv. KP-nummer of BIG-nummer
              </p>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Wachtwoord *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Min. 8 tekens"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Minimaal 8 tekens met hoofdletter, kleine letter en cijfer
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Bevestig Wachtwoord *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Herhaal wachtwoord"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Terms */}
            <div>
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className="mt-1 mr-2 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">
                  Ik ga akkoord met de{' '}
                  <Link to="/algemene-voorwaarden" className="text-purple-600 hover:text-purple-800" target="_blank">
                    algemene voorwaarden
                  </Link>{' '}
                  en het{' '}
                  <Link to="/privacy" className="text-purple-600 hover:text-purple-800" target="_blank">
                    privacybeleid
                  </Link>
                </span>
              </label>
              {errors.termsAccepted && (
                <p className="mt-1 text-sm text-red-600">{errors.termsAccepted}</p>
              )}
            </div>

            {/* Captcha */}
            <div>
              <Turnstile
                ref={turnstileRef}
                onVerify={(token) => {
                  setTurnstileToken(token);
                  setErrors(prev => ({ ...prev, turnstile: '' }));
                }}
                onExpire={() => setTurnstileToken('')}
              />
              {errors.turnstile && (
                <p className="mt-1 text-sm text-red-600">{errors.turnstile}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !turnstileToken}
              className={`w-full py-3 px-4 bg-purple-500 text-white font-semibold rounded-md transition-colors ${
                isSubmitting || !turnstileToken
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-purple-600'
              }`}
            >
              {isSubmitting ? 'Bezig met registreren...' : 'Account Aanmaken'}
            </button>
          </form>

          {/* Login Link */}
          <p className="mt-6 text-center text-sm text-gray-600">
            Heb je al een account?{' '}
            <Link to="/login" className="text-purple-600 hover:text-purple-800 font-medium">
              Log hier in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;